import { Stack } from '@cmg/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import { InstitutionalIndicationOrderType } from '../../../../../../../../graphql';
import { useIndicationActivitySyndicateContext } from '../../../../context/IndicationActivitySyndicateContext';
import { IndicationSectionWithEditButtons } from '../../common/indication-section-edit-view/IndicationSectionWithEditButtons';
import IndicationSectionForm from '../../common/indication-section-form/IndicationSectionForm';
import { getInitialTouchedFields } from '../../common/indication-section-form/IndicationSectionForm.model';
import { PassIndicationDialog } from '../../common/PassIndicationDialog';
import {
  IndicationFormValues,
  validateIndicationSection,
} from '../../common/utils/IndicationSectionValidation';
import { createInitialValues as createInitialValuesPass } from '../../sales-and-trading/indication-section-create-view/IndicationSectionCreateView.model';
import { usePassOnInstitutionalIndication } from '../common/pass-institutional-indication-dialog/hooks/usePassOnInstitutionalIndication';
import { useUpdateInstitutionalIndication } from './hooks/useUpdateInstitutionalIndication';
import { createInitialValues } from './IndicationSectionEditView.model';

export type Props = Readonly<{
  onFinishEditing: () => void;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionEditView: React.FC<Props> = ({ onFinishEditing }) => {
  const { offeringId, basicPublishedOffering, orderBookConfiguration, publishedOffering } =
    useIndicationActivitySyndicateContext();

  const offering = basicPublishedOffering!;
  const indication = offering.orderBook.institutionalIndication!;

  const [isPassDialogOpen, setIsPassDialogOpen] = React.useState(false);

  const [updateIndication] = useUpdateInstitutionalIndication(
    offeringId,
    indication,
    onFinishEditing
  );

  const convertibleFilingTalkRanges = useConvertibleFilingTalkRanges(
    defaultTo(publishedOffering?.terms.filings, null)
  );

  const initialValues =
    indication.type === InstitutionalIndicationOrderType.Pass
      ? createInitialValuesPass(offering)
      : createInitialValues({
          indication,
          convertibleFilingTalkRanges,
          offeringType: defaultTo(basicPublishedOffering?.type, null),
          isIndicationPriceAsPercentageParEnabled:
            orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
        });

  const formik = useFormik<IndicationFormValues>({
    initialValues,
    validate: values =>
      validateIndicationSection({
        values,
        isLimitIndicationsOnlyEnabled: orderBookConfiguration.isLimitIndicationsOnlyEnabled,
        isIndicationPriceAsPercentageParEnabled:
          orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
        isOnlySharesUnitOnIndicationsEnabled:
          orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled,
        isInvestorContactRequired: false,
        offeringType: offering.type,
        displayMissingContacts: false,
      }),
    validateOnMount: true,
    onSubmit: updateIndication,
    initialTouched: getInitialTouchedFields({
      interestLevels: initialValues.interestLevels,
      offeringType: offering.type,
      isIndicationPriceAsPercentageParEnabled:
        orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
    }),
  });
  const { isSubmitting, handleSubmit } = formik;

  const isPassButtonVisible = indication.type !== InstitutionalIndicationOrderType.Pass;

  const [onPass, isPassing] = usePassOnInstitutionalIndication({
    offeringId,
    indicationId: indication.id,
    indicationStatus: indication.status,
    onClose: () => setIsPassDialogOpen(false),
    onFinishEditing,
  });

  return (
    <IndicationSectionWithEditButtons
      indicationStatus={indication.status}
      isPassButtonVisible={isPassButtonVisible}
      isPassButtonDisabled={isSubmitting}
      handlePass={() => setIsPassDialogOpen(true)}
      isSubmitting={isSubmitting}
      handleDiscard={onFinishEditing}
      handleSubmit={handleSubmit}
    >
      <Stack gap={1}>
        <FormikProvider value={formik}>
          <Form>
            <IndicationSectionForm
              filings={defaultTo(publishedOffering?.terms.filings, null)}
              securityType={defaultTo(publishedOffering?.security.type, null)}
              tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
              instruments={defaultTo(publishedOffering?.instruments, null)}
              issuerName={defaultTo(publishedOffering?.issuer.name, null)}
              offeringType={offering.type}
              pricingCurrencyCode={offering.pricingCurrencyCode}
              canEnterTranche={indication.type === InstitutionalIndicationOrderType.Pass}
              securityTypeDisplayName={offering.securityTypeDisplayName!}
              isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
              isOnlySharesUnitOnIndicationsEnabled={
                orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
              }
              isIndicationPriceAsPercentageParEnabled={
                orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
              }
            />
          </Form>
        </FormikProvider>

        <PassIndicationDialog
          isOpen={isPassDialogOpen}
          onClose={() => setIsPassDialogOpen(false)}
          onPass={onPass}
          isLoading={isPassing}
        />
      </Stack>
    </IndicationSectionWithEditButtons>
  );
};

export default IndicationSectionEditView;
